import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    ReactFlow,
    ReactFlowProvider,
    Background,
    BackgroundVariant,
    Controls,
    MiniMap,
    addEdge,
    useNodesState,
    useEdgesState,
    useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import "./flow-designer.css";

import FlowNodeCard from "./FlowNodeCard";
import NodePalette from "./NodePalette";
import NodeConfigPanel from "./NodeConfigPanel";

const NODE_TYPES = { sitecentric: FlowNodeCard };

function uuid() {
    return crypto.randomUUID ? crypto.randomUUID() : `n-${Math.random().toString(36).slice(2, 10)}`;
}

function defaultNodeFor(type) {
    const base = {
        id: uuid(),
        name: titleCase(type),
        type,
        config: {},
        input_mapping: {},
        condition: null,
        retry: {},
        timeout_seconds: 120,
        continue_on_failure: false,
        parallel_group: null,
        position: { x: 0, y: 0 },
        description: null,
    };
    if (type === "agent") base.config = { agent_id: "" };
    if (type === "webhook") base.config = { url: "" };
    if (type === "api") base.config = { url: "", method: "GET" };
    if (type === "condition") base.config = { expression: "{{steps.x.output.ok}} == true" };
    if (type === "manual_approval") base.config = { message: "Please review and approve" };
    if (type === "protocol_intel") base.config = { mode: "full" };
    return base;
}

function titleCase(s) {
    return (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Convert backend nodes[]+edges[] -> ReactFlow nodes/edges.
 * Each node renders with a custom 'sitecentric' type so we control the visual.
 * Execution overlay (status/latency/cost/error) is merged into the node data.
 */
function nodesToFlow(nodes, edges, executionByNodeId = {}) {
    const rfNodes = (nodes || []).map((n, idx) => {
        const exec = executionByNodeId[n.id];
        return {
            id: n.id,
            type: "sitecentric",
            position: n.position && (n.position.x || n.position.y) ? n.position : { x: (idx % 4) * 280, y: Math.floor(idx / 4) * 160 },
            data: {
                ...n,
                execStatus: exec?.status,
                execError: exec?.error,
                execLatency: exec?.latency_ms,
                execCost: exec?.cost_usd,
            },
        };
    });
    const rfEdges = (edges || []).map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        type: e.type || "default",
        animated: !!e.animated,
        label: e.label,
    }));
    return { rfNodes, rfEdges };
}

function flowToBackend(rfNodes, rfEdges) {
    const nodes = rfNodes.map((rn) => {
        const d = rn.data || {};
        const out = {
            id: rn.id,
            name: d.name,
            type: d.type,
            config: d.config || {},
            input_mapping: d.input_mapping || {},
            condition: d.condition || null,
            retry: d.retry || {},
            timeout_seconds: d.timeout_seconds ?? 120,
            continue_on_failure: !!d.continue_on_failure,
            parallel_group: d.parallel_group || null,
            position: rn.position || { x: 0, y: 0 },
            description: d.description || null,
        };
        return out;
    });
    const edges = rfEdges.map((e) => ({
        id: e.id, source: e.source, target: e.target,
        label: e.label || null, animated: !!e.animated,
    }));
    return { nodes, edges };
}

export default function FlowCanvas({ flow, draft, setDraft, agents = [], execution = null, readOnly = false }) {
    return (
        <ReactFlowProvider>
            <FlowCanvasInner flow={flow} draft={draft} setDraft={setDraft} agents={agents} execution={execution} readOnly={readOnly} />
        </ReactFlowProvider>
    );
}

function FlowCanvasInner({ flow, draft, setDraft, agents, execution, readOnly }) {
    const wrapperRef = useRef(null);
    const reactFlow = useReactFlow();

    const executionByNodeId = useMemo(() => {
        if (!execution || !execution.node_executions) return {};
        const m = {};
        for (const ne of execution.node_executions) {
            // Always show LATEST attempt for a given node id
            const prev = m[ne.node_id];
            if (!prev || (ne.attempt || 0) >= (prev.attempt || 0)) m[ne.node_id] = ne;
        }
        return m;
    }, [execution]);

    const [initial] = useState(() => nodesToFlow(draft.nodes || [], draft.edges || [], executionByNodeId));
    const [rfNodes, setRfNodes, onNodesChange] = useNodesState(initial.rfNodes);
    const [rfEdges, setRfEdges, onEdgesChange] = useEdgesState(initial.rfEdges);
    const [selectedId, setSelectedId] = useState(null);

    // When draft changes EXTERNALLY (e.g. tab switch), rebuild canvas state
    // We track via a stamp derived from the draft.nodes ids.
    const draftStamp = useMemo(() => (draft.nodes || []).map((n) => n.id).join("|"), [draft.nodes]);
    const lastStampRef = useRef(draftStamp);
    useEffect(() => {
        if (draftStamp !== lastStampRef.current) {
            lastStampRef.current = draftStamp;
            const built = nodesToFlow(draft.nodes || [], draft.edges || [], executionByNodeId);
            setRfNodes(built.rfNodes);
            setRfEdges(built.rfEdges);
        }
    }, [draftStamp]); // eslint-disable-line

    // Live exec overlay — patch node.data.execStatus when execution updates
    useEffect(() => {
        setRfNodes((nodes) =>
            nodes.map((n) => {
                const exec = executionByNodeId[n.id];
                return {
                    ...n,
                    data: {
                        ...n.data,
                        execStatus: exec?.status,
                        execError: exec?.error,
                        execLatency: exec?.latency_ms,
                        execCost: exec?.cost_usd,
                    },
                };
            }),
        );
    }, [execution, executionByNodeId, setRfNodes]);

    // Keep parent draft in sync with canvas (debounced via React batching)
    const syncDraft = useCallback((nextNodes, nextEdges) => {
        const { nodes, edges } = flowToBackend(nextNodes, nextEdges);
        setDraft((d) => ({ ...d, nodes, edges }));
    }, [setDraft]);

    // Connect handler
    const onConnect = useCallback((params) => {
        const newEdge = { ...params, id: `e-${uuid()}`, animated: false };
        setRfEdges((eds) => {
            const next = addEdge(newEdge, eds);
            // sync after state
            setTimeout(() => {
                setRfNodes((rn) => { syncDraft(rn, next); return rn; });
            }, 0);
            return next;
        });
    }, [setRfEdges, setRfNodes, syncDraft]);

    // Drop handler — palette → canvas
    const onDragOver = useCallback((e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    }, []);
    const onDrop = useCallback((e) => {
        e.preventDefault();
        const type = e.dataTransfer.getData("application/sitecentric-node-type");
        if (!type) return;
        const bounds = wrapperRef.current.getBoundingClientRect();
        const position = reactFlow.screenToFlowPosition({
            x: e.clientX - bounds.left,
            y: e.clientY - bounds.top,
        });
        const newNode = defaultNodeFor(type);
        newNode.position = position;
        const rfNew = {
            id: newNode.id,
            type: "sitecentric",
            position,
            data: { ...newNode },
        };
        setRfNodes((nodes) => {
            const next = [...nodes, rfNew];
            setTimeout(() => {
                setRfEdges((eds) => { syncDraft(next, eds); return eds; });
            }, 0);
            return next;
        });
        setSelectedId(newNode.id);
    }, [reactFlow, setRfNodes, setRfEdges, syncDraft]);

    // After move (drag finishes) → sync positions back
    const onNodeDragStop = useCallback(() => {
        setRfEdges((eds) => { setRfNodes((rn) => { syncDraft(rn, eds); return rn; }); return eds; });
    }, [setRfNodes, setRfEdges, syncDraft]);

    // After delete (any node/edge removed via React Flow keyboard del)
    const onNodesDelete = useCallback((deleted) => {
        const ids = new Set(deleted.map((d) => d.id));
        setTimeout(() => {
            setRfEdges((eds) => {
                setRfNodes((rn) => { syncDraft(rn, eds); return rn; });
                return eds;
            });
        }, 0);
        if (selectedId && ids.has(selectedId)) setSelectedId(null);
    }, [setRfNodes, setRfEdges, syncDraft, selectedId]);
    const onEdgesDelete = useCallback(() => {
        setTimeout(() => {
            setRfEdges((eds) => { setRfNodes((rn) => { syncDraft(rn, eds); return rn; }); return eds; });
        }, 0);
    }, [setRfNodes, setRfEdges, syncDraft]);

    const onNodeClick = useCallback((_, node) => setSelectedId(node.id), []);
    const onPaneClick = useCallback(() => setSelectedId(null), []);

    const updateSelected = useCallback((updated) => {
        setRfNodes((nodes) => {
            const next = nodes.map((n) =>
                n.id === selectedId ? { ...n, data: { ...n.data, ...updated } } : n,
            );
            setTimeout(() => {
                setRfEdges((eds) => { syncDraft(next, eds); return eds; });
            }, 0);
            return next;
        });
    }, [selectedId, setRfNodes, setRfEdges, syncDraft]);

    const deleteSelected = useCallback(() => {
        if (!selectedId) return;
        setRfNodes((nodes) => {
            const next = nodes.filter((n) => n.id !== selectedId);
            setRfEdges((eds) => {
                const nextE = eds.filter((e) => e.source !== selectedId && e.target !== selectedId);
                setTimeout(() => syncDraft(next, nextE), 0);
                return nextE;
            });
            return next;
        });
        setSelectedId(null);
    }, [selectedId, setRfNodes, setRfEdges, syncDraft]);

    const selectedNode = useMemo(
        () => rfNodes.find((n) => n.id === selectedId)?.data,
        [rfNodes, selectedId],
    );

    // All non-selected nodes are potential upstream sources for the selected
    // node's templating dropdowns (skip-condition, condition expression, etc.).
    const upstreamNodes = useMemo(
        () => rfNodes.filter((n) => n.id !== selectedId).map((n) => ({ id: n.id, name: n.data?.name, type: n.data?.type })),
        [rfNodes, selectedId],
    );

    return (
        <div className="flex gap-3" data-testid="flow-canvas">
            {!readOnly && <NodePalette />}
            <div
                ref={wrapperRef}
                onDragOver={onDragOver}
                onDrop={onDrop}
                className="flex-1 h-[640px] border border-white/10 bg-[#050505] rounded-md overflow-hidden"
                data-testid="flow-canvas-pane"
            >
                <ReactFlow
                    nodes={rfNodes}
                    edges={rfEdges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onConnect={readOnly ? undefined : onConnect}
                    onNodeClick={onNodeClick}
                    onPaneClick={onPaneClick}
                    onNodeDragStop={onNodeDragStop}
                    onNodesDelete={onNodesDelete}
                    onEdgesDelete={onEdgesDelete}
                    nodeTypes={NODE_TYPES}
                    nodesDraggable={!readOnly}
                    nodesConnectable={!readOnly}
                    elementsSelectable
                    fitView
                    minZoom={0.2}
                    maxZoom={2}
                    proOptions={{ hideAttribution: false }}
                    deleteKeyCode={readOnly ? null : ["Backspace", "Delete"]}
                    selectionKeyCode={null}
                >
                    <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="rgba(255,255,255,0.06)" />
                    <Controls showInteractive={false} />
                    <MiniMap pannable nodeColor={() => "#7C3AED"} />
                </ReactFlow>
            </div>
            {!readOnly && selectedNode && (
                <NodeConfigPanel
                    node={selectedNode}
                    onChange={(patch) => updateSelected(patch)}
                    onDelete={deleteSelected}
                    agents={agents}
                    upstreamNodes={upstreamNodes}
                />
            )}
        </div>
    );
}
